/**
 * KB용 Dropbox 설정 팝업 모달
 * Step 1: 앱 생성 가이드
 * Step 2: App Key / App Secret 입력 + 저장
 * Step 3: Dropbox 계정 OAuth 연결
 */

import React, { useState, useEffect } from 'react';
import connectorApi from '../../api/connectors';
import { Button, Modal, useToast } from '../ui';
import dropboxLogo from '../../assets/icons/marketplace/dropbox.svg';
import CopyIcon from '../../assets/icons/common/copy.svg?react';
import RefreshIcon from '../../assets/icons/common/refresh.svg?react';

const KB_DROPBOX_REDIRECT_URI_EXAMPLE = 'https://acxion-on.etechcloud.co.kr/api/auth/kb-dropbox/callback';

interface KbDropboxConfigModalProps {
  open: boolean;
  onClose: () => void;
  onConnected?: (email: string) => void;
}

export function KbDropboxConfigModal({ open, onClose, onConnected }: KbDropboxConfigModalProps) {
  const { showToast } = useToast();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [appKey, setAppKey] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [connectedEmail, setConnectedEmail] = useState('');
  const [connectError, setConnectError] = useState('');

  // 모달 열릴 때 기존 설정 복원
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSaveError('');
    setConnectError('');
    connectorApi.getKbDropboxConfig().then(cfg => {
      if (cfg?.connected) {
        setConnected(true);
        setConnectedEmail(cfg.connectedEmail || '');
        setAppKey(cfg.clientId || '');
        setStep(3);
      } else if (cfg?.clientId) {
        setAppKey(cfg.clientId);
        setAppSecret(''); // 보안상 복원 안 함
        setStep(3);       // 저장됐으면 바로 연결 단계로
      } else {
        setStep(1);
      }
    }).catch(() => setStep(1))
      .finally(() => setLoading(false));
  }, [open]);

  // Step 2: App Key / Secret 저장 후 Step 3으로
  const handleSave = async () => {
    if (!appKey.trim() || !appSecret.trim()) return;
    setSaving(true); setSaveError('');
    try {
      await connectorApi.saveKbDropboxConfig(appKey.trim(), appSecret.trim());
      setStep(3);
    } catch {
      setSaveError('저장 실패. 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  // Step 3: Dropbox OAuth 팝업
  const handleOAuth = async () => {
    setConnectError('');
    try {
      const url = await connectorApi.getKbDropboxAuthUrl();
      const popup = window.open(url, 'kb-dropbox-auth', 'width=500,height=700');
      const handler = (e: MessageEvent) => {
        if (e.data?.type === 'KB_DROPBOX_CONNECTED') {
          window.removeEventListener('message', handler);
          popup?.close();
          setConnected(true);
          setConnectedEmail(e.data.email);
          onConnected?.(e.data.email);
        }
      };
      window.addEventListener('message', handler);
    } catch {
      setConnectError('인증 URL을 가져오는데 실패했습니다. 앱 설정을 다시 확인해주세요.');
    }
  };

  // Step 3: 연결 확인 (팝업 닫힌 후 수동 확인)
  const handleCheckConnection = async () => {
    try {
      const saved = await connectorApi.getKbDropboxConfig();
      if (saved?.connected) {
        setConnected(true);
        setConnectedEmail(saved.connectedEmail || '');
        onConnected?.(saved.connectedEmail || '');
      } else {
        setConnectError('아직 연결되지 않았습니다. 위 버튼을 먼저 클릭하고 로그인을 완료해주세요.');
      }
    } catch {
      setConnectError('연결 상태 확인 실패. 다시 시도해주세요.');
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Dropbox 연결 설정"
      size="sm"
      footerRight={
        <>
          {/* Step 1 */}
          {step === 1 && (
            <Button color="primary" onClick={() => setStep(2)}>다음</Button>
          )}
          {/* Step 2 */}
          {step === 2 && (
            <>
              <Button color="gray" appearance="outline" onClick={() => setStep(1)}>이전</Button>
              <Button
                color="primary"
                disabled={!appKey.trim() || !appSecret.trim() || saving}
                onClick={handleSave}
              >
                {saving ? '저장 중...' : '저장 후 다음'}
              </Button>
            </>
          )}
          {/* Step 3 */}
          {step === 3 && (
            <>
              {!connected && (
                <Button color="gray" appearance="outline" onClick={() => setStep(2)}>이전</Button>
              )}
              <Button color="primary" onClick={onClose}>
                {connected ? '완료' : '닫기'}
              </Button>
            </>
          )}
        </>
      }
    >
      {loading ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-3)' }}>설정 로드 중...</div>
      ) : (
        <div className="bedrock-guide">

          {/* ── Step 1: 앱 생성 가이드 ── */}
          {step === 1 && (
            <>
              <div className="bedrock-guide-section node-guide-head">
                <div className="bedrock-guide-title">Dropbox 앱 만들기</div>
                <p className="node-guide-desc">Dropbox 개발자 콘솔에서 앱을 만들고 App Key를 발급받습니다.</p>
              </div>
              <div className="bedrock-guide-section">
                <div className="bedrock-guide-step">
                  <span className="bedrock-guide-badge">1</span>
                  <div>
                    <strong>개발자 콘솔 접속</strong>
                    <p>
                      <a href="https://www.dropbox.com/developers/apps" target="_blank" rel="noreferrer">
                        dropbox.com/developers/apps
                      </a> → Create app
                    </p>
                  </div>
                </div>
                <div className="bedrock-guide-step">
                  <span className="bedrock-guide-badge">2</span>
                  <div>
                    <strong>앱 설정</strong>
                    <p>Scoped access → Full Dropbox → 앱 이름 입력 → Create app</p>
                  </div>
                </div>
                <div className="bedrock-guide-step">
                  <span className="bedrock-guide-badge">3</span>
                  <div>
                    <strong>Permissions 탭 → 권한 체크 후 Submit</strong>
                    <ul className="node-scope-list">
                      <li><code className="gmail-code">files.content.read</code></li>
                      <li><code className="gmail-code">files.content.write</code></li>
                      <li><code className="gmail-code">files.metadata.read</code></li>
                    </ul>
                  </div>
                </div>
                <div className="bedrock-guide-step">
                  <span className="bedrock-guide-badge">4</span>
                  <div className="node-guide-step-body">
                    <strong>Settings → OAuth 2 → Redirect URIs 추가</strong>
                    <div className="bedrock-ext-row node-copy-row">
                      <input className="input-control" type="text" value={KB_DROPBOX_REDIRECT_URI_EXAMPLE} readOnly />
                      <button
                        type="button"
                        className="bedrock-copy-btn node-copy-btn"
                        title="복사"
                        onClick={() => {
                          navigator.clipboard.writeText(KB_DROPBOX_REDIRECT_URI_EXAMPLE);
                          showToast('success', '복사됨');
                        }}
                      >
                        <CopyIcon />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── Step 2: App Key / App Secret 입력 ── */}
          {step === 2 && (
            <>
              <div className="bedrock-guide-section node-guide-head">
                <div className="bedrock-guide-title">App Key / App Secret 입력</div>
                <p className="node-guide-desc">Settings 탭에서 App key와 App secret을 복사하여 입력합니다.</p>
              </div>
              <div className="bedrock-guide-section">
                <div className="bedrock-guide-title">App Key <span className="required">*</span></div>
                <div className="input">
                  <input
                    type="text"
                    className="input-control"
                    placeholder="xxxxxxxxxxxxxxxxxxxxxx"
                    value={appKey}
                    onChange={e => setAppKey(e.target.value)}
                  />
                </div>
              </div>
              <div className="bedrock-guide-section">
                <div className="bedrock-guide-title">App Secret <span className="required">*</span></div>
                <div className="input">
                  <input
                    type="password"
                    className="input-control"
                    placeholder="xxxxxxxxxxxxxxxxxxxxxx"
                    value={appSecret}
                    onChange={e => setAppSecret(e.target.value)}
                  />
                </div>
                {saveError && <div className="node-save-error">{saveError}</div>}
              </div>
            </>
          )}

          {/* ── Step 3: Dropbox 계정 연결 ── */}
          {step === 3 && !connected && (
            <>
              <div className="bedrock-guide-section node-guide-head">
                <div className="bedrock-guide-title">Dropbox 계정 연결</div>
                <p className="node-guide-desc">Dropbox에 로그인하여 파일 접근 권한을 허용합니다.</p>
              </div>
              <div className="bedrock-guide-section">
                <div className="bedrock-guide-title">1. 허용되는 권한</div>
                <div className="node-permission-list">
                  <div className="node-permission-item" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    ✓ 파일 읽기 / 업로드
                  </div>
                  <div className="node-permission-item" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    ✓ 파일 목록 조회
                  </div>
                </div>
              </div>
              <div className="bedrock-guide-section">
                <div className="bedrock-guide-title">2. Dropbox 로그인</div>
                <button
                  type="button"
                  className="btn-google node-oauth-btn"
                  style={{ background: '#0061FF', color: '#fff', border: 'none' }}
                  onClick={handleOAuth}
                >
                  <img src={dropboxLogo} alt="" style={{ width: 16, height: 16, marginRight: 8 }} />
                  Dropbox 계정으로 연결
                </button>
                <div style={{ marginTop: 12 }}>
                  <Button color="primary" appearance="outline" size="sm" onClick={handleCheckConnection}>
                    연결 확인
                  </Button>
                </div>
                {connectError && (
                  <div className="node-save-error" style={{ marginTop: 8 }}>{connectError}</div>
                )}
              </div>
              <div className="bedrock-guide-section">
                <div className="bedrock-guide-title" style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  App Key를 변경하려면 "이전"을 클릭하세요.
                </div>
              </div>
            </>
          )}

          {/* 연결 완료 */}
          {step === 3 && connected && (
            <div className="bedrock-guide-section">
              <div className="node-account-card bedrock-guide-step">
                <div className="node-account-info">
                  <img src={dropboxLogo} alt="" style={{ width: 22, height: 22, marginRight: 8, flexShrink: 0 }} />
                  <div className="node-account-text">
                    <strong>{connectedEmail}</strong>
                    <span>Dropbox 계정이 연결되어 있습니다</span>
                  </div>
                </div>
                <Button color="gray" appearance="outline" size="sm" onClick={async () => {
                  setConnected(false);
                  setConnectedEmail('');
                }}>
                  <RefreshIcon /> 재연결
                </Button>
              </div>
            </div>
          )}

        </div>
      )}
    </Modal>
  );
}
